import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Search,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Video,
} from "lucide-react";

import ReusableHeader from "../../../../../reusableField/ReusableHeader.js";
import DemographicJPFitmentScenario from "../DemographicJPFitmentScenario/DemographicJPFitmentScenario.js";
import BackIcon from "../../../../../Images/backIcon.png";
import HomeIcon from "../../../../../Images/homeIcon.png";
import { useQuery } from "@apollo/client";
import { Role_Account_Query } from "../../../../../services/userAPI";
import { Modal, message, Spin } from "antd";
import EditIcon from "../../../../../Images/editIcon.png";
import DeleteIcon from "../../../../../Images/deleteIcon2.png";
import { Globe } from "lucide-react";

import {
  getScenarios,
  deleteScenario,
} from "../../../../../services/SyncApis";


const ScenarioBasedAssessment = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [deleteModalOpen, setDeleteModalOpen] =
    useState(false);

  const [deleteScenarioId, setDeleteScenarioId] =
    useState(null);

  const [scenarios, setScenarios] =
    useState([]);

  const [loading, setLoading] =
    useState(false);

  const [searchQuery, setSearchQuery] =
    useState("");

  const [currentPage, setCurrentPage] =
    useState(1);

  const [totalPages, setTotalPages] =
    useState(1);

  const pageSize = 5;

  const fullState =
    location.state || {};

  const {
    roleId,
    accountId,
    name,
    accountName = "Admin",
  } = fullState;

  const safeRoleName =
    name ||
    "Experience Configuration";


  const { data: roleData } =
    useQuery(
      Role_Account_Query,
      {
        variables: {
          accountId: accountId
            ? parseInt(accountId)
            : null,
        },

        fetchPolicy:
          "network-only",

        skip:
          !accountId,
      }
    );


  const currentRole =
    roleData?.getAllJobrole?.find(
      (r) =>
        r.id === roleId
    );


  /* ============================================================
   * GET SCENARIOS
   * ============================================================ */

  const fetchScenariosList =
    async () => {

      if (!roleId) {
        return;
      }

      if (!accountId) {
        message.error(
          "Client ID is missing."
        );
        return;
      }

      setLoading(true);

      try {

        const response =
          await getScenarios(
            Number(roleId),
            {
              adminClientId:
                Number(accountId),

              page:
                Number(currentPage),

              limit:
                Number(pageSize),

              search:
                searchQuery.trim(),
            }
          );


        /*
         * Backend response:
         *
         * {
         *   success: true,
         *   data: [...],
         *   pagination: {...}
         * }
         */

        const scenarioData =
          response?.data;


        setScenarios(
          Array.isArray(
            scenarioData
          )
            ? scenarioData
            : []
        );

        const pagination = response?.pagination || {};
        const reportedTotalPages = Number(
          pagination.totalPages ?? pagination.total_pages ?? pagination.pages
        );
        const reportedTotalItems = Number(
          pagination.totalItems ?? pagination.total_items ?? pagination.totalCount ?? pagination.total
        );
        const nextTotalPages = Number.isFinite(reportedTotalPages) && reportedTotalPages > 0
          ? reportedTotalPages
          : Number.isFinite(reportedTotalItems) && reportedTotalItems >= 0
            ? Math.max(1, Math.ceil(reportedTotalItems / pageSize))
            : typeof pagination.hasNextPage === "boolean"
              ? pagination.hasNextPage ? currentPage + 1 : currentPage
              : scenarioData?.length === pageSize
                ? currentPage + 1
                : scenarioData?.length === 0 && currentPage > 1
                  ? currentPage - 1
                  : currentPage;

        setTotalPages(Math.max(1, nextTotalPages));
        if (currentPage > nextTotalPages) {
          setCurrentPage(Math.max(1, nextTotalPages));
        }

      } catch (error) {

        console.error(
          "Error fetching scenarios:",
          error
        );

        const apiMessage =
          error?.response?.data
            ?.message;

        message.error(
          apiMessage ||
          "Failed to fetch scenarios."
        );

        setScenarios([]);
        setTotalPages(1);

      } finally {

        setLoading(false);
      }
    };


  /*
   * ------------------------------------------------------------
   * Load scenarios whenever:
   *
   * role changes
   * client changes
   * page changes
   * search changes
   * ------------------------------------------------------------
   */

  useEffect(() => {

    fetchScenariosList();

  }, [
    roleId,
    accountId,
    currentPage,
    searchQuery,
  ]);


  /* ============================================================
   * DELETE
   * ============================================================ */

  const handleDelete =
    async () => {

      if (
        !deleteScenarioId ||
        !roleId ||
        !accountId
      ) {
        return;
      }

      try {

        await deleteScenario(
          Number(roleId),
          deleteScenarioId,
          {
            adminClientId:
              Number(accountId),

            /*
             * changedBy can be added here later
             * when the actual numeric admin user ID
             * is available.
             */
          }
        );

        message.success(
          "Scenario deleted successfully"
        );

        setDeleteModalOpen(
          false
        );

        setDeleteScenarioId(
          null
        );

        fetchScenariosList();

      } catch (error) {

        console.error(
          "Error deleting scenario:",
          error
        );

        const apiMessage =
          error?.response?.data
            ?.message;

        message.error(
          apiMessage ||
          "Failed to delete scenario."
        );
      }
    };


  /*
   * ============================================================
   * Backend already handles pagination and search.
   *
   * Therefore we do NOT filter/slice the data again here.
   * ============================================================
   */

  return (
    <div className="min-h-screen bg-[#F6F6F6]">

      <ReusableHeader />


      <div className="px-8 pb-8">


        {/* Top Section */}

        <div className="mb-4 -mt-5">

          {/* Back & Home Buttons */}

          <div className="flex justify-between items-center w-full mt-6 ml-1">

            {/* Back Button */}

            <button
              onClick={() =>
                navigate(
                  "/sync-configuration",
                  {
                    state:
                      fullState,
                  }
                )
              }
              className="flex items-center gap-2 bg-[#C21A17] text-white px-5 py-2 rounded-lg text-sm font-semibold shadow-md hover:bg-[#A91513] transition-all duration-200"
            >

              <img
                src={BackIcon}
                alt="Back"
                className="w-[15px] h-[15px] object-contain"
              />

              Back

            </button>


            {/* Home Button */}

            <button
              onClick={() =>
                navigate(
                  "/admin"
                )
              }
              className="flex items-center gap-2 bg-[#C21A17] text-white px-5 py-2 rounded-lg text-sm font-semibold shadow-md hover:bg-[#A91513] transition-all duration-200"
            >

              <img
                src={HomeIcon}
                alt="Home"
                className="w-[15px] h-[15px] object-contain"
              />

              Home

            </button>

          </div>


          {/* Breadcrumb */}

          <div className="mt-3 ml-2 flex items-center flex-wrap text-[14px] text-[#7A7A7A] gap-1">

            <span
              onClick={() =>
                navigate("/admin")
              }
              className="cursor-pointer"
            >
              {accountName}
            </span>

            <span className="mx-1">
              ›
            </span>

            <span
              onClick={() =>
                navigate("/dashboard", {
                  state: fullState,
                })
              }
              className="cursor-pointer"
            >
              {fullState?.name || ""}
            </span>

            <span className="mx-1">
              ›
            </span>

            <span
              onClick={() =>
                navigate("/experience-configuration", {
                  state: fullState,
                })
              }
              className="cursor-pointer"
            >
              Experience Configuration
            </span>

            <span className="mx-1">
              ›
            </span>

            <span
              onClick={() =>
                navigate("/productsConfiguration", {
                  state: fullState,
                })
              }
              className="cursor-pointer"
            >
              Product Configuration
            </span>

            <span className="mx-1">
              ›
            </span>

            <span
              onClick={() =>
                navigate("/sync-configuration", {
                  state: fullState,
                })
              }
              className="cursor-pointer"
            >
              Sync
            </span>

            <span className="mx-1">
              ›
            </span>

            <span
              className="
      font-semibold
      text-[#1B1B1B]
    "
            >
              Assessment Configuration
            </span>

          </div>

        </div>


        <DemographicJPFitmentScenario />


        {/* Card */}

        <div className="bg-white rounded-xl shadow-sm p-4">

          <h2 className="text-[17px] font-semibold text-[#1B1B1B]">
            Scenario-Based Assessment
          </h2>


          {/* Search */}

          <div className="flex items-center gap-3 mt-4">

            <div className="flex-1 relative">

              <input
                type="text"
                placeholder="Search Scenario"
                value={
                  searchQuery
                }
                onChange={(e) => {

                  setSearchQuery(
                    e.target.value
                  );

                  setCurrentPage(
                    1
                  );

                }}
                className="w-full h-[40px] border border-gray-300 rounded-[6px] pl-3 pr-10 outline-none text-sm"
              />

              <Search
                size={16}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-black"
              />

            </div>


            <button
              onClick={() =>
                navigate(
                  "/Add-Scenario",
                  {
                    state:
                      fullState,
                  }
                )
              }
              className="bg-[#AD0101] text-white px-5 h-[40px] rounded-[6px] text-sm font-medium hover:bg-red-700"
            >
              Add Scenario
            </button>

          </div>


          {/* Scenario Cards */}

          {loading ? (

            <div className="flex justify-center items-center h-[140px]">

              <Spin
                size="large"
              />

            </div>

          ) : scenarios.length === 0 ? (

            <div className="h-[140px] flex items-center justify-center text-[#B5B5B5] text-sm">
              No Data Available
            </div>

          ) : (

            scenarios.map(
              (scenario) => (

                <div
                  key={
                    scenario.id ||
                    scenario._id
                  }
                  className="mt-2 h-[96px] bg-white border border-[#E7E7E7] rounded-[6px] overflow-hidden flex items-stretch justify-between"
                >

                  {/* Left Section */}

                  <div className="flex items-center min-w-0 flex-1">

                    {/* Thumbnail */}

                    <div className="w-[160px] self-stretch bg-[#E8B2B2] flex items-center justify-center overflow-hidden shrink-0">

                      {scenario.video?.thumbnailCode ? (
                        <img
                          src={scenario.video.thumbnailCode}
                          alt={scenario.video?.title || "Scenario thumbnail"}
                          className="w-full h-full object-cover block"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-white/30 flex items-center justify-center">

                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            fill="white"
                            className="w-5 h-5 ml-1"
                          >
                            <path d="M8 5v14l11-7z" />
                          </svg>

                        </div>
                      )}

                    </div>


                    {/* Content */}

                    <div className="min-w-0 px-3 py-2">

                      <span className="inline-block px-2 py-0.5 text-[10px] font-medium text-[#6B7280] bg-[#F3F4F6] rounded-full">

                        {
                          scenario.scenarioTypeId === 1
                            ? "Video"
                            : ""
                        }

                      </span>


                      <h3 className="mt-1 text-[14px] font-semibold text-[#1F2937] truncate">

                        {
                          scenario.video?.title ||
                          "Untitled"
                        }

                      </h3>


                      <div className="flex items-center gap-2 mt-1 text-[12px] text-[#7A7A7A]">

                        <div className="flex items-center gap-1">

                          <Video
                            size={14}
                            strokeWidth={
                              1.8
                            }
                            className="text-[#8C8C8C]"
                          />

                          <span>
                            Video
                          </span>

                        </div>


                        <div className="flex items-center gap-1">

                          <Globe
                            size={14}
                            strokeWidth={
                              1.8
                            }
                            className="text-[#8C8C8C]"
                          />

                          <span>
                            {scenario.questions?.reduce(
                              (total, question) =>
                                total + Number(question.weightage || 0),
                              0
                            )}{" "}
                            marks
                          </span>

                        </div>

                      </div>

                    </div>

                  </div>


                  {/* Right Section */}

                  <div className="flex flex-col items-end h-full px-2 py-2 shrink-0">

                    <span
                      className={`px-2 py-0.5 text-[10px] font-semibold rounded-full ${scenario.isActive !==
                        false
                        ? "bg-[#EAF8EA] text-[#22A447]"
                        : "bg-[#FEE2E2] text-[#DC2626]"
                        }`}
                    >
                      {scenario.isActive !==
                        false
                        ? "Active"
                        : "Inactive"}
                    </span>


                    <div className="flex gap-2 mt-4">

                      <button
                        type="button"
                        aria-label="Edit scenario"
                        style={{
                          width: 28,
                          height: 28,
                          minWidth: 28,
                          minHeight: 28,
                          padding: 0,
                          border: 0,
                          borderRadius: "6px",
                          background: "#F3F4F6",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          cursor: "pointer",
                        }}
                        onClick={() =>
                          navigate(
                            "/Add-Scenario",
                            {
                              state: {
                                ...fullState,

                                scenarioId:
                                  scenario.id ||
                                  scenario._id,

                                editMode:
                                  true,
                              },
                            }
                          )
                        }
                      >
                        <img
                          src={EditIcon}
                          alt=""
                          style={{
                            width: 15,
                            height: 18,
                            objectFit: "contain",
                          }}
                        />
                      </button>


                      <button
                        type="button"
                        onClick={() => {

                          setDeleteScenarioId(
                            scenario.id ||
                            scenario._id
                          );

                          setDeleteModalOpen(
                            true
                          );

                        }}
                        style={{
                          width: 28,
                          height: 28,
                          minWidth: 28,
                          minHeight: 28,
                          padding: 0,
                          border: 0,
                          borderRadius: "6px",
                          background: "#F3F4F6",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          cursor: "pointer",
                        }}
                      >

                        <img
                          src={
                            DeleteIcon
                          }
                          alt="Delete"
                          style={{
                            width: 28,
                            height: 28,
                            objectFit: "contain",
                          }}
                        />

                      </button>

                    </div>

                  </div>

                </div>

              )
            )

          )}


          {/* Delete Modal */}

          <Modal
            open={
              deleteModalOpen
            }
            footer={null}
            closable={false}
            centered
            width="min(480px, calc(100vw - 32px))"
            styles={{
              content: { padding: 18 },
              body: { padding: 0 },
            }}
            onCancel={() =>
              setDeleteModalOpen(
                false
              )
            }
          >

            <div className="px-0 py-0">

              <h2 className="text-[18px] font-semibold text-[#2F2F2F]">
                Are you sure you want to delete this Scenario?
              </h2>

              <p className="mt-2 text-[14px] leading-6 text-[#555]">
                This will permanently delete the scenario, video, and quiz. This action cannot be undone.
              </p>

              <p className="mt-2 text-[14px] text-[#444]">
                Are you{" "}
                <span className="font-semibold">
                  Sure?
                </span>
              </p>

              <div className="flex justify-end gap-3 mt-3">

                <button
                  onClick={() => {

                    setDeleteModalOpen(
                      false
                    );

                    setDeleteScenarioId(
                      null
                    );

                  }}
                  className="w-[96px] h-[38px] border border-gray-400 rounded-[6px] text-sm text-gray-600 font-medium "
                >
                  Cancel
                </button>


                <button
                  onClick={
                    handleDelete
                  }
                  className="w-[104px] h-[38px] rounded-[6px] bg-[#AD0101] text-sm text-white font-medium "
                >
                  Delete
                </button>

              </div>

            </div>

          </Modal>


          {/* Pagination */}

          <div className="border-t pt-3 flex justify-end items-center gap-2">
            <button type="button" aria-label="First page" disabled={currentPage === 1} className="text-gray-700 disabled:text-gray-300" onClick={() => setCurrentPage(1)}><ChevronsLeft size={18} /></button>
            <button type="button" aria-label="Previous page" disabled={currentPage === 1} className="text-gray-700 disabled:text-gray-300" onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}><ChevronLeft size={18} /></button>
            <span aria-current="page" className="flex h-8 min-w-8 items-center justify-center rounded-md border border-gray-300 bg-white px-2 text-sm text-[#C40000]">{currentPage}</span>
            <button type="button" aria-label="Next page" disabled={currentPage >= totalPages} className="text-gray-700 disabled:text-gray-300" onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}><ChevronRight size={18} /></button>
            <button type="button" aria-label="Last page" disabled={currentPage >= totalPages} className="text-gray-700 disabled:text-gray-300" onClick={() => setCurrentPage(totalPages)}><ChevronsRight size={18} /></button>
          </div>

        </div>

      </div>

    </div>
  );
};


export default ScenarioBasedAssessment;